/**
 * CanvasComments — comment threads pinned to the board.
 *
 * Renders three things while comment mode is on (activeTool === 'comment'):
 *   • numbered pins over the board, following pan/zoom (screen = scene·zoom + pan)
 *   • a "new comment" popover where the board was clicked
 *   • the Comments panel — Open / Resolved / All filter, search, the thread
 *     list (numbers match the pins), and the selected thread's replies
 *
 * Fabric never holds comment objects: pins are DOM over the canvas, positions
 * are scene coords stored on the thread (backend: /api/canvas/:id/comments).
 * Peers get changes live: after every write we announce over the collab
 * socket and everyone refetches (collab.commentsTick).
 *
 * Every button/input here sets its own size, margin, padding and line-height:
 * the global index.css rules pin buttons and inputs to 40px with side padding.
 */
import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import { ChevronDown, Check, CheckCircle2, RotateCcw, Trash2, Send, X, Search } from 'lucide-react';
import { T } from '../../../components/em/ui';
import { toast } from '../../../utils/toast';
import { confirmDialog } from '../../../components/em/confirm';
import canvasApi from './canvasApi';

const PIN = 30;                 // pin box size (px)
const ORANGE = '#f54b01';       // draft (a comment not posted yet)
const GREY = '#94a3b8';         // resolved
const PANEL_W = 340;

// One colour per author — picked from the user id, so the same person keeps
// the same colour on every board and every device. All dark enough for the
// white pin number.
const USER_COLORS = [
  '#2563eb', // blue
  '#7c3aed', // violet
  '#db2777', // pink
  '#0d9488', // teal
  '#d97706', // amber
  '#16a34a', // green
  '#dc2626', // red
  '#0891b2', // cyan
  '#9333ea', // purple
  '#4f46e5', // indigo
];
const colorForUser = (userId) => {
  const n = Number(userId);
  return USER_COLORS[(Number.isFinite(n) ? Math.abs(n) : 0) % USER_COLORS.length];
};
// Soft halo for the selected pin, in the author's own colour.
const halo = (hex, alpha) => {
  const v = parseInt(hex.slice(1), 16);
  return `rgba(${(v >> 16) & 255},${(v >> 8) & 255},${v & 255},${alpha})`;
};

const FILTERS = [
  { key: 'open', label: 'Open' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'all', label: 'All' },
];

// "now", "10m", "1h", "3d", then a short date.
const relTime = (ts) => {
  if (!ts) return '';
  const then = new Date(ts).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, (Date.now() - then) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d`;
  return new Date(ts).toLocaleDateString([], { day: 'numeric', month: 'short' });
};

const me = () => {
  try {
    const u = JSON.parse(localStorage.getItem('userdata') || '{}');
    return { id: Number(u.id) || null, admin: localStorage.getItem('role') === 'Admin' };
  } catch {
    return { id: null, admin: false };
  }
};

const resetBtn = { margin: 0, lineHeight: 'normal', boxSizing: 'border-box' };

// Small text button used in the thread actions row.
function ActBtn({ icon: Icon, children, onClick, danger, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...resetBtn, display: 'inline-flex', alignItems: 'center', gap: 5, height: 28,
        padding: '0 10px', borderRadius: 8, cursor: disabled ? 'default' : 'pointer',
        border: `1px solid ${danger ? '#fecaca' : T.border}`, background: T.bgCard,
        color: danger ? T.danger : T.fg2, font: `600 11.5px ${T.font}`, opacity: disabled ? 0.5 : 1,
      }}
    >
      {Icon && <Icon size={13} />} {children}
    </button>
  );
}

// Textarea + send; Enter posts, Shift+Enter breaks the line, Esc cancels.
function Composer({ placeholder, onSubmit, onCancel, autoFocus, busy, submitLabel = 'Comment' }) {
  const [text, setText] = useState('');
  const ref = useRef(null);
  useEffect(() => { if (autoFocus) ref.current?.focus(); }, [autoFocus]);
  const send = async () => {
    const v = text.trim();
    if (!v || busy) return;
    const ok = await onSubmit(v);
    if (ok !== false) setText('');
  };
  return (
    <div>
      <textarea
        ref={ref}
        rows={2}
        value={text}
        maxLength={2000}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation(); // keep canvas shortcuts (Delete, V, …) out of the box
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
          if (e.key === 'Escape') { e.preventDefault(); onCancel?.(); }
        }}
        style={{
          width: '100%', boxSizing: 'border-box', margin: 0, height: 'auto', minHeight: 56,
          padding: '8px 10px', resize: 'vertical', borderRadius: 9, outline: 'none',
          border: `1px solid ${T.border}`, background: T.bgCard, color: T.fg1,
          font: `400 13px/1.45 ${T.font}`,
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 6 }}>
        {onCancel && <ActBtn onClick={onCancel}>Cancel</ActBtn>}
        <button
          type="button"
          onClick={send}
          disabled={!text.trim() || busy}
          style={{
            ...resetBtn, display: 'inline-flex', alignItems: 'center', gap: 5, height: 28,
            padding: '0 12px', borderRadius: 8, border: 0,
            cursor: !text.trim() || busy ? 'default' : 'pointer',
            background: T.gradient || ORANGE, color: '#fff', font: `600 11.5px ${T.font}`,
            opacity: !text.trim() || busy ? 0.55 : 1,
          }}
        >
          <Send size={12} /> {busy ? 'Posting…' : submitLabel}
        </button>
      </div>
    </div>
  );
}

// Open / Resolved / All dropdown (custom: the native <select> list is unstyled).
function FilterMenu({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const cur = FILTERS.find((f) => f.key === value) || FILTERS[0];
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          ...resetBtn, display: 'inline-flex', alignItems: 'center', gap: 6, height: 30,
          padding: '0 10px', borderRadius: 8, border: `1px solid ${T.border}`,
          background: T.bgCard, color: T.fg2, cursor: 'pointer', font: `500 12.5px ${T.font}`,
        }}
      >
        {cur.label} <ChevronDown size={13} />
      </button>
      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 5, minWidth: 120,
            padding: 4, borderRadius: 9, background: T.bgCard, border: `1px solid ${T.border}`,
            boxShadow: '0 10px 24px -10px rgba(10,10,10,0.3)',
          }}
        >
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              role="option"
              aria-selected={f.key === value}
              onClick={() => { onChange(f.key); setOpen(false); }}
              style={{
                ...resetBtn, width: '100%', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', height: 30, padding: '0 9px', borderRadius: 6,
                border: 0, cursor: 'pointer', textAlign: 'left',
                background: f.key === value ? T.orange50 : 'transparent',
                color: f.key === value ? T.orange : T.fg2,
                font: `${f.key === value ? 600 : 500} 12.5px ${T.font}`,
              }}
            >
              {f.label} {f.key === value && <Check size={13} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const Badge = ({ n, color }) => (
  <span
    style={{
      width: 22, height: 22, borderRadius: 9999, flexShrink: 0, background: color, color: '#fff',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      font: `700 11px ${T.font}`,
    }}
  >
    {n}
  </span>
);

const CanvasComments = forwardRef(function CanvasComments(
  { canvasId, canvasRef, open, ownerId, tick, onChanged, onCountChange },
  ref,
) {
  const [threads, setThreads] = useState([]);
  const [filter, setFilter] = useState('open');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);   // { x, y } scene point of a new comment
  const [hoverId, setHoverId] = useState(null); // pin under the pointer (name tooltip)
  // When I last opened this board's comments (server-stored, so it holds across
  // devices). undefined = not loaded yet, null = never opened.
  const [readAt, setReadAt] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [vpt, setVpt] = useState([1, 0, 0, 1, 0, 0]);
  const user = useMemo(me, []);

  // ── Data ─────────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      const list = await canvasApi.listComments(canvasId);
      setThreads(Array.isArray(list) ? list : []);
    } catch {
      /* comments are secondary — never block the board */
    }
  }, [canvasId]);
  useEffect(() => { load(); }, [load, tick]);

  useEffect(() => {
    let alive = true;
    setReadAt(undefined);
    canvasApi.getCommentsRead(canvasId)
      .then((r) => { if (alive) setReadAt(r?.read_at || null); })
      .catch(() => { if (alive) setReadAt(null); });
    return () => { alive = false; };
  }, [canvasId]);

  // Toolbar badge: UNREAD comments by OTHER people (threads + replies) on
  // threads that are still open — posted after I last opened the comments.
  // My own comments never count, and opening comment mode clears it.
  const unreadCount = useMemo(() => {
    if (readAt === undefined) return 0; // read state still loading
    const since = readAt ? new Date(readAt).getTime() : -Infinity;
    const unread = (c) =>
      Number(c.user_id) !== user.id && new Date(c.created_at).getTime() > since;
    return threads
      .filter((t) => !t.resolved)
      .reduce((n, t) => n + (unread(t) ? 1 : 0) + t.replies.filter(unread).length, 0);
  }, [threads, readAt, user.id]);
  useEffect(() => { onCountChange?.(unreadCount); }, [unreadCount, onCountChange]);

  // Seen = read: while the comments are open, anything unread (including
  // comments arriving live) is marked read on the server.
  useEffect(() => {
    if (!open || unreadCount === 0) return;
    canvasApi.markCommentsRead(canvasId)
      .then((r) => setReadAt(r?.read_at || new Date().toISOString()))
      .catch(() => {});
  }, [open, unreadCount, canvasId]);

  // A thread deleted elsewhere can't stay selected.
  useEffect(() => {
    if (selectedId && !threads.some((t) => t.id === selectedId)) setSelectedId(null);
  }, [threads, selectedId]);

  // Leaving comment mode drops the draft.
  useEffect(() => { if (!open) setDraft(null); }, [open]);

  // Filtered list — its order numbers the pins ("Numbered pins match list order").
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return threads.filter((t) => {
      if (filter === 'open' && t.resolved) return false;
      if (filter === 'resolved' && !t.resolved) return false;
      if (!q) return true;
      const hay = [t.body, t.author_name, ...t.replies.map((r) => `${r.body} ${r.author_name}`)]
        .join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [threads, filter, query]);

  // ── Viewport tracking (pins follow pan / zoom) ───────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    let raf;
    let last = '';
    const loop = () => {
      const v = canvasRef.current?.getViewportTransform?.();
      if (v) {
        const k = v.join(',');
        if (k !== last) { last = k; setVpt(v); }
      }
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, [open, canvasRef]);

  const toScreen = useCallback((x, y) => ({ x: x * vpt[0] + vpt[4], y: y * vpt[3] + vpt[5] }), [vpt]);

  // Bring a thread's pin into view if it's off-screen (or behind the panel).
  const reveal = useCallback((t) => {
    const p = toScreen(t.x, t.y);
    const w = window.innerWidth - PANEL_W - 40;
    if (p.x < 40 || p.y < 90 || p.x > w || p.y > window.innerHeight - 100) {
      canvasRef.current?.panToScenePoint?.(t.x, t.y);
    }
  }, [toScreen, canvasRef]);

  // ── Board click in comment mode (from CanvasCore via the editor) ────────────
  // First click clears an open draft/selection; the next one starts a comment.
  useImperativeHandle(ref, () => ({
    handleCanvasPoint: (p) => {
      if (draft || selectedId) { setDraft(null); setSelectedId(null); return; }
      setDraft({ x: p.x, y: p.y });
    },
  }), [draft, selectedId]);

  // ── Writes ───────────────────────────────────────────────────────────────────
  const afterWrite = async () => { await load(); onChanged?.(); };
  const fail = (e, msg) => toast.error(e?.response?.data?.error || msg);

  const postNew = async (body) => {
    if (!draft) return false;
    setBusy(true);
    try {
      const t = await canvasApi.addComment(canvasId, { body, x: draft.x, y: draft.y });
      setDraft(null);
      setFilter((f) => (f === 'resolved' ? 'open' : f)); // keep the new thread visible
      setSelectedId(t.id);
      await afterWrite();
      return true;
    } catch (e) { fail(e, 'Could not add comment'); return false; } finally { setBusy(false); }
  };

  const postReply = async (threadId, body) => {
    setBusy(true);
    try { await canvasApi.replyComment(canvasId, threadId, body); await afterWrite(); return true; } catch (e) { fail(e, 'Could not reply'); return false; } finally { setBusy(false); }
  };

  const setResolved = async (t, resolved) => {
    try {
      await canvasApi.updateComment(canvasId, t.id, { resolved });
      if (resolved && filter === 'open') setSelectedId(null);
      await afterWrite();
    } catch (e) { fail(e, 'Could not update comment'); }
  };

  const remove = async (c, isThread) => {
    if (!(await confirmDialog(isThread ? 'Delete this comment and all its replies?' : 'Delete this reply?'))) return;
    try {
      await canvasApi.deleteComment(canvasId, c.id);
      if (isThread) setSelectedId(null);
      await afterWrite();
    } catch (e) { fail(e, 'Could not delete comment'); }
  };

  const canDelete = (c) =>
    user.admin || Number(c.user_id) === user.id || Number(ownerId) === user.id;

  if (!open) return null;

  // ── Render ───────────────────────────────────────────────────────────────────
  // Pin + badge colour: the author's colour; grey once resolved.
  const colorFor = (t) => (t.resolved ? GREY : colorForUser(t.user_id));
  const draftScreen = draft ? toScreen(draft.x, draft.y) : null;

  return (
    <>
      {/* Pins */}
      <div className="cv-comment-layer">
        {visible.map((t, i) => {
          const p = toScreen(t.x, t.y);
          const sel = t.id === selectedId;
          return (
            <button
              key={t.id}
              type="button"
              className="cv-comment-pin"
              aria-label={`Comment ${i + 1} by ${t.author_name}`}
              onMouseEnter={() => setHoverId(t.id)}
              onMouseLeave={() => setHoverId((h) => (h === t.id ? null : h))}
              onFocus={() => setHoverId(t.id)}
              onBlur={() => setHoverId((h) => (h === t.id ? null : h))}
              onClick={(e) => { e.stopPropagation(); setDraft(null); setSelectedId(sel ? null : t.id); }}
              style={{
                left: p.x, top: p.y - PIN, width: PIN, height: PIN, background: colorFor(t),
                boxShadow: sel
                  ? `0 0 0 4px ${halo(colorFor(t), 0.25)}, 0 4px 10px -3px ${halo(colorFor(t), 0.6)}`
                  : '0 3px 8px -3px rgba(15,23,42,0.45)',
                transform: sel ? 'scale(1.12)' : undefined,
                transformOrigin: 'bottom left',
                zIndex: sel ? 2 : 1,
              }}
            >
              {i + 1}
            </button>
          );
        })}

        {/* Hover tooltip — who left this comment. Above the pin; flips below
            when the pin is near the top of the board. */}
        {(() => {
          const t = hoverId != null && visible.find((x) => x.id === hoverId);
          if (!t) return null;
          const p = toScreen(t.x, t.y);
          const below = p.y - PIN < 60;
          const n = t.replies.length;
          return (
            <div
              className="cv-comment-tip"
              role="tooltip"
              style={{
                left: p.x + PIN / 2,
                top: below ? p.y + 8 : p.y - PIN - 8,
                transform: below ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
              }}
            >
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 9999, background: colorFor(t), marginRight: 6, flexShrink: 0 }} />
              {t.author_name}
              {n > 0 && <span style={{ opacity: 0.7, fontWeight: 500 }}>&nbsp;· {n} {n === 1 ? 'reply' : 'replies'}</span>}
            </div>
          );
        })()}

        {/* New-comment draft: pin + popover */}
        {draftScreen && (
          <>
            <div
              className="cv-comment-pin"
              style={{
                left: draftScreen.x, top: draftScreen.y - PIN, width: PIN, height: PIN,
                background: ORANGE, boxShadow: '0 0 0 4px rgba(245,75,1,0.22)', zIndex: 3,
              }}
            >
              +
            </div>
            <div
              className="cv-comment-popover"
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                left: Math.max(16, Math.min(draftScreen.x + PIN + 8, window.innerWidth - PANEL_W - 300)),
                top: Math.max(80, Math.min(draftScreen.y - PIN, window.innerHeight - 200)),
                background: T.bgCard, border: `1px solid ${T.border}`,
              }}
            >
              <Composer
                autoFocus
                busy={busy}
                placeholder="Add a comment…"
                onSubmit={postNew}
                onCancel={() => setDraft(null)}
              />
            </div>
          </>
        )}
      </div>

      {/* Panel */}
      <div className="cv-comments-wrap">
        <div className="cv-comments" style={{ background: T.bgCard, border: `1px solid ${T.border}` }}>
          <div className="cv-comments-head" style={{ borderBottom: `1px solid ${T.border}` }}>
            <span style={{ font: `700 14px ${T.font}`, color: T.fg3 }}>Comments</span>
            <FilterMenu value={filter} onChange={(f) => { setFilter(f); setSelectedId(null); }} />
          </div>

          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.border}` }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} color={T.fg7} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Search comments"
                style={{
                  width: '100%', boxSizing: 'border-box', margin: 0, height: 36,
                  padding: '0 12px 0 32px', borderRadius: 9, outline: 'none',
                  border: `1px solid ${T.border}`, background: T.bgCard, color: T.fg1,
                  font: `400 13px ${T.font}`,
                }}
              />
            </div>
          </div>

          <div className="cv-comments-list">
            {visible.length === 0 && (
              <div style={{ padding: '28px 18px', textAlign: 'center', font: `400 12.5px/1.5 ${T.font}`, color: T.fg5 }}>
                {query.trim()
                  ? 'No comments match your search.'
                  : filter === 'resolved'
                    ? 'No resolved comments yet.'
                    : 'No comments yet — click anywhere on the canvas to add one.'}
              </div>
            )}

            {visible.map((t, i) => {
              const sel = t.id === selectedId;
              const n = t.replies.length;
              return (
                <div key={t.id} style={{ background: sel ? T.orange50 : 'transparent', borderBottom: `1px solid ${T.border}` }}>
                  <button
                    type="button"
                    onClick={() => {
                      setDraft(null);
                      if (sel) { setSelectedId(null); return; }
                      setSelectedId(t.id); reveal(t);
                    }}
                    style={{
                      ...resetBtn, display: 'block', width: '100%', height: 'auto', padding: '14px 18px',
                      border: 0, background: 'transparent', textAlign: 'left', cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <Badge n={i + 1} color={colorFor(t)} />
                      <span style={{ font: `700 13px ${T.font}`, color: T.fg1 }}>{t.author_name}</span>
                      <span style={{ font: `400 12px ${T.font}`, color: T.fg7 }}>{relTime(t.created_at)}</span>
                      {t.resolved && (
                        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 3, font: `600 11px ${T.font}`, color: '#008236' }}>
                          <CheckCircle2 size={12} /> Resolved
                        </span>
                      )}
                    </div>
                    <div style={{ font: `400 13.5px/1.45 ${T.font}`, color: T.fg2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {t.body}
                    </div>
                    {n > 0 && !sel && (
                      <div style={{ marginTop: 6, font: `500 12.5px ${T.font}`, color: T.orange }}>
                        {n} {n === 1 ? 'reply' : 'replies'}
                      </div>
                    )}
                  </button>

                  {/* Selected thread: replies, reply box, actions */}
                  {sel && (
                    <div style={{ padding: '0 18px 14px' }}>
                      {t.replies.map((r) => (
                        <div key={r.id} style={{ padding: '9px 0 9px 12px', borderLeft: `3px solid ${colorForUser(r.user_id)}`, marginBottom: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                            <span style={{ font: `700 12.5px ${T.font}`, color: T.fg1 }}>{r.author_name}</span>
                            <span style={{ font: `400 11.5px ${T.font}`, color: T.fg7 }}>{relTime(r.created_at)}</span>
                            {canDelete(r) && (
                              <button
                                type="button"
                                aria-label="Delete reply"
                                onClick={() => remove(r, false)}
                                style={{ ...resetBtn, marginLeft: 'auto', width: 22, height: 22, padding: 0, border: 0, borderRadius: 6, background: 'transparent', color: T.fg7, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                              >
                                <X size={13} />
                              </button>
                            )}
                          </div>
                          <div style={{ font: `400 13px/1.45 ${T.font}`, color: T.fg2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{r.body}</div>
                        </div>
                      ))}

                      {!t.resolved && (
                        <div style={{ marginTop: 8 }}>
                          <Composer
                            busy={busy}
                            placeholder="Reply…"
                            submitLabel="Reply"
                            onSubmit={(body) => postReply(t.id, body)}
                          />
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                        {t.resolved ? (
                          <ActBtn icon={RotateCcw} onClick={() => setResolved(t, false)}>Reopen</ActBtn>
                        ) : (
                          <ActBtn icon={CheckCircle2} onClick={() => setResolved(t, true)}>Resolve</ActBtn>
                        )}
                        {canDelete(t) && (
                          <ActBtn icon={Trash2} danger onClick={() => remove(t, true)}>Delete</ActBtn>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="cv-comments-foot" style={{ borderTop: `1px solid ${T.border}`, font: `400 12px ${T.font}`, color: T.fg5 }}>
            Numbered pins match list order
          </div>
        </div>
      </div>
    </>
  );
});

export default CanvasComments;
